import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Smartphone, MoreVertical, Plus, Share, CheckCircle } from 'lucide-react'

const isInstalled =
  window.matchMedia('(display-mode: standalone)').matches ||
  (navigator as unknown as { standalone?: boolean }).standalone === true

type Platform = 'android' | 'ios'

function detectPlatform(): Platform {
  if (/iphone|ipad|ipod/i.test(navigator.userAgent)) return 'ios'
  return 'android'
}

interface Step {
  icon: React.ReactNode
  text: string
}

function StepList({ steps }: { steps: Step[] }) {
  return (
    <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
      {steps.map((step, i) => (
        <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <span
            style={{
              minWidth: 28,
              height: 28,
              borderRadius: '50%',
              background: 'var(--brand)',
              color: 'var(--on-brand)',
              fontFamily: 'var(--font-sans)',
              fontWeight: 700,
              fontSize: 'var(--fs-sm)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {i + 1}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 4 }}>
            <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}>{step.icon}</span>
            <span
              style={{
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--fs-body)',
                color: 'var(--text-body)',
                lineHeight: 'var(--lh-normal)',
              }}
            >
              {step.text}
            </span>
          </div>
        </li>
      ))}
    </ol>
  )
}

export function HowToInstallPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [platform, setPlatform] = useState<Platform>(detectPlatform)

  const androidSteps: Step[] = [
    { icon: <Smartphone size={18} />, text: t('settings.install_step_android_1') },
    { icon: <MoreVertical size={18} />, text: t('settings.install_step_android_2') },
    { icon: <Plus size={18} />, text: t('settings.install_step_android_3') },
    { icon: <Plus size={18} />, text: t('settings.install_step_android_4') },
  ]

  const iosSteps: Step[] = [
    { icon: <Smartphone size={18} />, text: t('settings.install_step_ios_1') },
    { icon: <Share size={18} />, text: t('settings.install_step_ios_2') },
    { icon: <Plus size={18} />, text: t('settings.install_step_ios_3') },
    { icon: <Plus size={18} />, text: t('settings.install_step_ios_4') },
  ]

  const pillBase: React.CSSProperties = {
    border: '1.5px solid var(--brand)',
    borderRadius: 'var(--radius-pill)',
    padding: '6px 18px',
    fontFamily: 'var(--font-sans)',
    fontWeight: 700,
    fontSize: 'var(--fs-sm)',
    cursor: 'pointer',
  }

  return (
    <div style={{ maxWidth: 'var(--feed-max)', margin: '0 auto', padding: '16px 16px 80px' }}>
      {/* Back */}
      <button
        type="button"
        onClick={() => navigate(-1)}
        style={{
          border: 'none',
          background: 'none',
          padding: 0,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          color: 'var(--text-muted)',
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--fs-sm)',
          marginBottom: 20,
        }}
      >
        <ArrowLeft size={18} />
        {t('common.back')}
      </button>

      {/* Already installed */}
      {isInstalled ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 16,
            padding: '48px 24px',
            textAlign: 'center',
            background: 'var(--surface)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-card)',
          }}
        >
          <CheckCircle size={48} color="var(--brand)" strokeWidth={1.5} />
          <p
            style={{
              margin: 0,
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--fs-body)',
              color: 'var(--text-body)',
              lineHeight: 'var(--lh-normal)',
              maxWidth: 300,
            }}
          >
            {t('settings.already_installed')}
          </p>
        </div>
      ) : (
        <div
          style={{
            background: 'var(--surface)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-card)',
            padding: 20,
          }}
        >
          {/* Title */}
          <h1
            style={{
              margin: '0 0 4px',
              fontFamily: 'var(--font-sans)',
              fontWeight: 700,
              fontSize: 'var(--fs-h2)',
              color: 'var(--text-strong)',
              letterSpacing: '-0.02em',
            }}
          >
            {t('settings.how_to_install_title')}
          </h1>
          <p
            style={{
              margin: '0 0 24px',
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--fs-sm)',
              color: 'var(--text-muted)',
            }}
          >
            {t('settings.how_to_install_subtitle')}
          </p>

          {/* Platform toggle */}
          <div style={{ marginBottom: 28 }}>
            <span
              style={{
                display: 'block',
                fontFamily: 'var(--font-sans)',
                fontWeight: 600,
                fontSize: 'var(--fs-sm)',
                color: 'var(--text-muted)',
                marginBottom: 10,
              }}
            >
              {t('settings.show_instructions_for')}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={() => setPlatform('android')}
                style={{
                  ...pillBase,
                  background: platform === 'android' ? 'var(--brand)' : 'transparent',
                  color: platform === 'android' ? 'var(--on-brand)' : 'var(--brand)',
                }}
              >
                {t('settings.platform_android')}
              </button>
              <button
                type="button"
                onClick={() => setPlatform('ios')}
                style={{
                  ...pillBase,
                  background: platform === 'ios' ? 'var(--brand)' : 'transparent',
                  color: platform === 'ios' ? 'var(--on-brand)' : 'var(--brand)',
                }}
              >
                {t('settings.platform_ios')}
              </button>
            </div>
          </div>

          {/* Steps */}
          <StepList steps={platform === 'android' ? androidSteps : iosSteps} />
        </div>
      )}
    </div>
  )
}
